import React from "react";
import { Card, CardBody, Col } from "reactstrap";
import CountUp from "react-countup";
import Skeleton from "react-loading-skeleton";

import { ActivityReportData } from "./types";

/** Up/down indicator against the preceding window of equal length. */
const Delta = ({
  value,
  comparable,
}: {
  value: number;
  comparable: boolean;
}) => {
  if (!comparable) {
    return <span className="text-muted fs-12">no comparable period</span>;
  }

  const flat = value === 0;
  const up = value > 0;
  const tone = flat ? "text-muted" : up ? "text-success" : "text-danger";
  const icon = flat
    ? "ri-subtract-line"
    : up
    ? "ri-arrow-right-up-line"
    : "ri-arrow-right-down-line";

  return (
    <span className={`${tone} fs-12`}>
      <i className={`${icon} align-middle me-1`}></i>
      {Math.abs(value)}%<span className="text-muted"> vs. previous</span>
    </span>
  );
};

const StatTile = ({
  label,
  value,
  decimals,
  change,
  comparable,
  icon,
  tone,
  loading,
}: {
  label: string;
  value: number;
  decimals?: number;
  change: number;
  comparable: boolean;
  icon: string;
  tone: string;
  loading: boolean;
}) => (
  <Col xl={3} md={6}>
    <Card className="card-animate">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1 overflow-hidden">
            <p className="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">
              {label}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="avatar-xs">
              <span
                className={`avatar-title bg-${tone}-subtle text-${tone} rounded-circle fs-16`}
              >
                <i className={icon}></i>
              </span>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between mt-3">
          <div className="w-100">
            {loading ? (
              <Skeleton height={28} width="60%" />
            ) : (
              <h4 className="fs-22 fw-semibold mb-2">
                <CountUp
                  start={0}
                  end={value}
                  decimals={decimals || 0}
                  duration={2}
                />
              </h4>
            )}
            {loading ? (
              <Skeleton height={12} width="40%" />
            ) : (
              <Delta value={change} comparable={comparable} />
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const EMPTY = {
  visits: 0,
  activeUsers: 0,
  pagesVisited: 0,
  activeAgencies: 0,
  visitsPerUser: 0,
};

const SummaryTiles = ({
  data,
  comparable,
  loading,
}: {
  data: ActivityReportData | null;
  comparable: boolean;
  loading: boolean;
}) => {
  const current = data?.summary?.current || EMPTY;
  const change = data?.summary?.change || EMPTY;

  return (
    <React.Fragment>
      <StatTile
        label="Page Visits"
        value={current.visits}
        change={change.visits}
        comparable={comparable}
        icon="ri-eye-line"
        tone="primary"
        loading={loading}
      />
      <StatTile
        label="Active Users"
        value={current.activeUsers}
        change={change.activeUsers}
        comparable={comparable}
        icon="ri-user-follow-line"
        tone="success"
        loading={loading}
      />
      <StatTile
        label="Visits per User"
        value={current.visitsPerUser}
        decimals={1}
        change={change.visitsPerUser}
        comparable={comparable}
        icon="ri-repeat-line"
        tone="info"
        loading={loading}
      />
      <StatTile
        label="Active Agencies"
        value={current.activeAgencies}
        change={change.activeAgencies}
        comparable={comparable}
        icon="ri-building-line"
        tone="warning"
        loading={loading}
      />
    </React.Fragment>
  );
};

export default SummaryTiles;
